import React from 'react'
import {
  PiArrowLeft,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
} from 'react-icons/pi'
import { cn } from 'components/UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { ds2026DropdownContentClass } from 'components/FbUI/ds2026DropdownMenu'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { ComposeMode } from './ComposePanel'
import {
  EmailDealActionCallbacks,
  buildActionContext,
  useEmailThreadActions,
} from './emailActions'
import { EmailActionMenuItems } from 'components/Email'

interface EmailDetailActionBarProps extends EmailDealActionCallbacks {
  threadIndex: number
  totalThreads: number
  pageSize: number
  pageStart: number
  onBack: () => void
  onNavigateThread: (delta: number) => void
  onCompose?: (mode: ComposeMode) => void
  assignDealSubContent?: React.ReactNode
  isHidden?: boolean
  isOwnThread?: boolean
}

/** Action bar for the email detail view with back button, actions dropdown, and pagination. */
export function EmailDetailActionBar({
  threadIndex,
  totalThreads,
  pageSize,
  pageStart,
  onBack,
  onNavigateThread,
  onCompose,
  assignDealSubContent,
  isHidden,
  isOwnThread,
  ...dealCallbacks
}: EmailDetailActionBarProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const actions = useEmailThreadActions(
    buildActionContext(
      dealCallbacks,
      onCompose,
      assignDealSubContent,
      isHidden,
      isOwnThread
    )
  )

  const pageEnd = Math.min(pageStart + pageSize - 1, totalThreads)
  const hasPrev = threadIndex > 0
  const hasNext = threadIndex < pageEnd - pageStart

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          className="flex items-center justify-center size-7 rounded-md border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg text-ds2026-btn-outline-text transition-colors hover:bg-ds2026-btn-outline-hover-bg hover:text-ds2026-btn-outline-hover-text"
          title="Back to list"
        >
          <PiArrowLeft className="size-4" />
        </button>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 h-7 px-1.5 rounded-md text-brand-bright text-sm font-medium transition-colors hover:text-brand-active data-[state=open]:text-brand-active"
            >
              <span>Actions</span>
              <PiCaretDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            style={{ minWidth: 200, zIndex }}
            className={ds2026DropdownContentClass}
          >
            <EmailActionMenuItems actions={actions} />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Pagination */}
      <div className="flex items-center gap-3 text-sm text-neutral-500">
        <span className="tabular-nums">
          {totalThreads > 0 ? `${pageStart}\u2013${pageEnd}` : '0'} of{' '}
          {totalThreads.toLocaleString()}
        </span>
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => onNavigateThread(-1)}
          className={cn(
            'flex items-center justify-center size-[38px] rounded-full transition-colors',
            hasPrev
              ? 'hover:bg-black/5 text-neutral-500 hover:text-neutral-800'
              : 'text-neutral-400 cursor-not-allowed'
          )}
        >
          <PiCaretLeft className="size-[22px]" />
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onNavigateThread(1)}
          className={cn(
            'flex items-center justify-center size-[38px] rounded-full transition-colors',
            hasNext
              ? 'hover:bg-black/5 text-neutral-500 hover:text-neutral-800'
              : 'text-neutral-400 cursor-not-allowed'
          )}
        >
          <PiCaretRight className="size-[22px]" />
        </button>
      </div>
    </div>
  )
}
